<template>
  <div :style="rootStyle">
    <h1 v-if="cfg.displayName" class="display-name-header" :style="{ color: accentColor }">{{ cfg.displayName }}</h1>
    <div class="tournament-main-row">
      <!-- Payouts Table (left column) - keep placeholder even when empty so center stays aligned -->
      <div class="main-col main-col-left">
        <template v-if="showPayouts && payoutState.numberOfPayouts > 0 && payoutState.totalPrizePool > 0 && summaryState.totalPlayers > 0">
          <h2 class="payouts-title">Payouts</h2>
          <transition-group name="payout-scroll" tag="table" class="payouts-table">
            <tr v-for="(payout, index) in payoutState.visiblePayouts" :key="payout.place || index">
              <td class="payout-place">{{ getPlaceDisplay(payout, index) }}</td>
              <td class="payout-amount">
                <span class="currency">$</span>
                <span class="amount">{{ (payout.amount ?? 0).toLocaleString() }}</span>
              </td>
            </tr>
          </transition-group>
        </template>
      </div>

      <!-- Timer and Blinds (center and right columns) -->
      <div class="timer-blinds-wrapper">
        <div class="timer-container">
          <h2 v-if="showLevel && !timerState.showPreCountdownScreen" class="level-heading">
            Level
            <v-btn v-if="isEditable" icon size="small" @click="$emit('decrement-level')" :disabled="level <= 1"><v-icon>mdi-minus</v-icon></v-btn>
            <span class="level-count">{{ timerState.isCurrentLevelBreak ? 'BREAK' : timerState.normalLevelCount }}</span>
            <v-btn v-if="isEditable" icon size="small" @click="$emit('increment-level')"><v-icon>mdi-plus</v-icon></v-btn>
          </h2>
          <div class="countdown-timer-wrapper">
            <v-btn
              v-if="isEditable && !timerState.timerRunning && !timerState.showPreCountdownScreen"
              icon
              size="small"
              variant="text"
              color="primary"
              class="timer-edit-btn"
              title="Edit countdown time"
              @click="$emit('edit-timer')"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <div class="countdown-timer">
              <span class="timer-minutes">{{ minutes }}</span>
              <span class="timer-colon">:</span>
              <span class="timer-seconds">{{ seconds }}</span>
            </div>
          </div>
        </div>

        <!-- Blinds (right column) - hidden during pre-countdown -->
        <div v-if="!timerState.showPreCountdownScreen && showBlinds" :class="['main-col', 'main-col-right', 'blinds-container']" :style="blindScaleFactor < 1 ? { '--blind-scale': blindScaleFactor } : {}">
          <template v-if="timerState.isCurrentLevelBreak">
            <div class="break-right-message">{{ levelState.currentLevelData?.breakMessage || 'Players are on break' }}</div>
          </template>
          <template v-else-if="levelState.currentLevelData">
            <!-- Small Blind and Big Blind on same line -->
            <div class="blinds-section">
              <div class="blinds-label">Small Blind / Big Blind</div>
              <div class="blinds-values">
                <div class="small-blind">{{ levelState.currentLevelData.smallBlind }}</div>
                <div class="blinds-separator">/</div>
                <div class="big-blind">{{ levelState.currentLevelData.bigBlind }}</div>
              </div>
            </div>
            <!-- Ante underneath -->
            <div v-if="levelState.currentLevelData.bigBlindAnte > 0" class="ante-section">
              <div class="blinds-label">
                <span v-if="levelState.currentLevelData.useBBAnte">Big Blind Ante</span>
                <span v-else>Ante</span>
              </div>
              <div class="ante-value">{{ levelState.currentLevelData.bigBlindAnte }}</div>
            </div>
          </template>
          <template v-else>
            <div class="blinds-placeholder">&nbsp;</div>
          </template>
        </div>
        <!-- Placeholder to keep timer centered during pre-countdown -->
        <div v-else class="main-col main-col-right"></div>
      </div>

      
    </div>

    <!-- If it's a break, show a break notice underneath the main row (kept outside the flex row so it doesn't change horizontal layout) -->
    <div v-if="timerState.isCurrentLevelBreak" class="break-container">
      <h2 class="break-title">🛑 BREAK</h2>
      <div>{{ levelState.currentLevelData?.breakMessage || 'Players are on break - no blinds in play' }}</div>
      <div v-if="showNextLevel && levelState.nextLevelData" class="break-next-level" :style="{ '--m-size': mNextLevel }">
        <div class="stats-label">Next Level</div>
        <div class="next-level-small">
          <strong>{{ levelState.nextLevelData.smallBlind }}</strong> / <strong>{{ levelState.nextLevelData.bigBlind }}</strong>
          <template v-if="levelState.nextLevelData.bigBlindAnte > 0">
            &nbsp;/ <span class="ante-label">Ante:</span> <strong>{{ levelState.nextLevelData.bigBlindAnte }}</strong>
          </template>
        </div>
      </div>
    </div>

    <!-- Last-rebuy notice is aligned above the Average Chip Stack column; host announcement stacks independently and neither suppresses the other -->
    <div v-if="showRebuyNotice" class="rebuy-notice-row">
      <div class="announcement-line rebuy-notice-line" :style="{ color: accentColor }">Last rebuy this level</div>
    </div>
    <div v-if="announcement?.text" class="announcement-line" :style="{ color: accentColor }">{{ announcement.text }}</div>

    <!-- Pre-countdown: show only a centered "Tournament Starting in" banner -->
    <div v-if="timerState.showPreCountdownScreen" class="tournament-stats-row pre-countdown-banner">
      <div class="pre-countdown-label">Until Tournament Starts</div>
    </div>

    <!-- Normal stats row -->
    <div v-else-if="anyStatsVisible" :class="['tournament-stats-row', statsRowClass]">
      <div v-if="showPrizePool" class="stats-col stats-col-left" :style="{ '--m-size': mPrizePool }">
        <div class="stats-label">Total Prize Pool</div>
        <div class="stats-value secondary-value">${{ summaryState.totalPot.toLocaleString() }}</div>
      </div>
      <div v-if="showAvgStack" class="stats-col stats-col-center" :style="{ '--m-size': mAvgStack }">
        <div class="stats-label">Average Chip Stack</div>
        <div class="stats-value secondary-value">{{ summaryState.averageChips?.toLocaleString?.() ?? 0 }}</div>
      </div>
      <div v-if="showNextLevel" class="stats-col stats-col-right" :style="{ '--m-size': mNextLevel }">
        <div class="stats-label">Next Level SB/BB</div>
        <div class="stats-value secondary-value next-level-small" v-if="levelState.nextLevelData">
          <strong>{{ levelState.nextLevelData.smallBlind }}</strong> / <strong>{{ levelState.nextLevelData.bigBlind }}</strong>
          <template v-if="levelState.nextLevelData.bigBlindAnte > 0">
            / <span class="ante-label">Ante:</span> <strong>{{ levelState.nextLevelData.bigBlindAnte }}</strong>
          </template>
        </div>
        <div class="stats-value secondary-value" v-else>—</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, toRef } from 'vue';
import { mergeDisplayConfig, SIZE_MULTIPLIERS } from '@/utils/displayConfig';

const props = defineProps({
  isEditable: { type: Boolean, default: false },
  level: Number, // Only needed for isEditable
  timerState: { type: Object, required: true },
  levelState: { type: Object, required: true },
  payoutState: { type: Object, required: true },
  summaryState: { type: Object, required: true },
  announcement: { type: Object, default: null },
  displayConfig: { type: Object, default: null },
  rebuyEndLevel: { type: Number, default: null },
});

// Keep prop reactivity by using toRef
const isEditable = toRef(props, 'isEditable');
const level = toRef(props, 'level');
const timerState = toRef(props, 'timerState');
const levelState = toRef(props, 'levelState');
const payoutState = toRef(props, 'payoutState');
const summaryState = toRef(props, 'summaryState');
const announcement = toRef(props, 'announcement');

// Merge so structures/tournaments predating displayConfig (or partial configs) render like today.
const cfg = computed(() => mergeDisplayConfig(props.displayConfig));
const accentColor = computed(() => cfg.value.accentColor || '#1565c0');

const showLevel = computed(() => cfg.value.modules.level.show);
const showBlinds = computed(() => cfg.value.modules.blinds.show);
const showPayouts = computed(() => cfg.value.modules.payouts.show);
const showPrizePool = computed(() => cfg.value.modules.prizePool.show);
const showAvgStack = computed(() => cfg.value.modules.avgStack.show);
const showNextLevel = computed(() => cfg.value.modules.nextLevel.show);
const anyStatsVisible = computed(() => showPrizePool.value || showAvgStack.value || showNextLevel.value);

// Derived entirely from the existing rebuy-cutoff setting; no host data entry.
const showRebuyNotice = computed(() => (
  cfg.value.showLastRebuyNotice &&
  !!props.rebuyEndLevel &&
  timerState.value.normalLevelCount === props.rebuyEndLevel
));
// The three-column grid only lines up when all three columns are present; otherwise
// fall back to the base flex row so the remaining columns re-center themselves.
const statsRowClass = computed(() => (
  showPrizePool.value && showAvgStack.value && showNextLevel.value ? 'three-stats-row' : ''
));

const moduleMultiplier = (key) => SIZE_MULTIPLIERS[cfg.value.modules[key]?.size] ?? 1;
const mLevel = computed(() => moduleMultiplier('level'));
const mTimer = computed(() => moduleMultiplier('timer'));
const mBlinds = computed(() => moduleMultiplier('blinds'));
const mPayouts = computed(() => moduleMultiplier('payouts'));
const mPrizePool = computed(() => moduleMultiplier('prizePool'));
const mAvgStack = computed(() => moduleMultiplier('avgStack'));
const mNextLevel = computed(() => moduleMultiplier('nextLevel'));

// Set once at the root so these cascade through every breakpoint's media-query
// rules without needing to be redeclared per breakpoint.
const rootStyle = computed(() => ({
  '--m-level': mLevel.value,
  '--m-timer': mTimer.value,
  '--m-blinds': mBlinds.value,
  '--m-payouts': mPayouts.value,
}));

defineEmits(['decrement-level', 'increment-level', 'edit-timer']);

const getPlaceDisplay = (payout, index) => {
  const place = payout.place || index + 1;
  if (place % 100 >= 11 && place % 100 <= 13) {
    return `${place}th`;
  }
  switch (place % 10) {
    case 1: return `${place}st`;
    case 2: return `${place}nd`;
    case 3: return `${place}rd`;
    default: return `${place}th`;
  }
};

// Scale blind font size down as digit count grows so values don't overflow the column.
// --blind-scale is applied inline on the blinds container and multiplied into --blind-size in CSS.
const blindScaleFactor = computed(() => {
  const data = levelState.value?.currentLevelData;
  if (!data) return 1;
  const maxDigits = Math.max(
    String(data.smallBlind  ?? 0).replace(/\D/g, '').length,
    String(data.bigBlind    ?? 0).replace(/\D/g, '').length,
    3 // never scale up beyond 3-digit base
  );
  if (maxDigits <= 3) return 1;
  if (maxDigits === 4) return 0.82;
  if (maxDigits === 5) return 0.62;
  if (maxDigits === 6) return 0.50;
  return 0.42; // 7+ digits
});

const minutes = computed(() => {
  const t = timerState.value?.formattedTime ?? '--:--';
  const parts = String(t).split(':');
  return parts[0] ?? '--';
});

const seconds = computed(() => {
  const t = timerState.value?.formattedTime ?? '--:--';
  const parts = String(t).split(':');
  return parts[1] ?? '--';
});

</script>

<style scoped>
/* --- Base Layout --- */
.tournament-main-row,
.tournament-stats-row {
  display: flex; /* Use flexbox for layout */
  align-items: center;
  justify-content: center;
  gap: 32px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}
.tournament-main-row {
  --blind-size: 4rem;
  margin-bottom: 24px;
}
.tournament-stats-row {
  margin-bottom: 8px;
  font-size: 1rem;
  color: #888;
  text-align: center;
}
.main-col-left,
.stats-col-left {
  flex-basis: 260px; /* Match TournamentView widths so columns line up */
  flex-shrink: 0;
  text-align: left;
}
.main-col-right,
.stats-col-right {
  flex-basis: 260px; /* Match TournamentView widths so columns line up */
  flex-shrink: 0;
  text-align: right;
}
.timer-blinds-wrapper {
  flex: 1 1 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1; /* Allow center to grow and push sides */
  /* Match the main row's gap so the timer's right inset (blinds col) equals its
     left inset (payouts col + row gap) and the timer centers on the page. */
  gap: inherit;
}
.timer-container,
.break-container,
.stats-col-center {
  flex: 1 1 0;
  min-width: 160px;
  text-align: center;
}

/* Three-column stats row: use a grid so center column is truly centered
   relative to the overall container and side columns match main layout widths. */
.three-stats-row {
  display: grid;
  grid-template-columns: 260px 1fr 260px;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 8px;
}

/* More specific selector to override other scoped styles that set flex
   on .tournament-stats-row; using the combined class increases specificity
   so the grid layout applies when both classes are present. */
.tournament-stats-row.three-stats-row {
  display: grid !important;
  /* Symmetric side tracks (matching the main row's 260px flex-basis columns) so
     the center column lines up with the timer's true center. */
  grid-template-columns: 260px 1fr 260px;
  align-items: center;
  gap: 24px;
}
.three-stats-row .stats-col-left,
.three-stats-row .stats-col-center,
.three-stats-row .stats-col-right {
  min-width: 220px;
}
.three-stats-row .stats-col-left { justify-self: start; text-align: left; }
.three-stats-row .stats-col-center { justify-self: center; text-align: center; }
.three-stats-row .stats-col-right { justify-self: center; text-align: center; }

/* --- Payouts --- */
.main-col-left {
  display: flex;
  flex-direction: column;
  align-items: center; /* Center the payout table under its title */
}
.payouts-title {
  font-size: calc(1.2rem * var(--m-payouts, 1));
  margin-bottom: 8px;
  text-align: center;
  width: 100%;
}
.payouts-table {
  transition: all 0.5s;
}
.payout-place { font-weight: bold; width: 40px; font-size: calc(1.6rem * var(--m-payouts, 1)); }
.payout-amount { width: 100px; font-size: calc(1.6rem * var(--m-payouts, 1)); text-align: right; }
.payout-amount .currency { margin-right: 0.5ch; display: inline-block; }
.payout-amount .amount { display: inline-block; text-align: right; min-width: 48px; }

/* --- Timer & Blinds --- */
.level-heading {
  font-size: calc(2.5rem * var(--m-level, 1));
  font-weight: normal;
  color: #333;
}
.break-container h2 {
  font-size: calc(1.2rem * var(--m-timer, 1));
  font-weight: normal;
  color: #333;
}
.level-count {
  margin: 0 8px;
  font-size: calc(2.5rem * var(--m-level, 1));
  font-weight: 900;
}
.countdown-timer-wrapper {
  display: inline-flex;
  align-items: center;
  position: relative;
}
.timer-edit-btn {
  position: absolute;
  right: calc(100% + 4px);
  top: 50%;
  transform: translateY(-50%);
}
.countdown-timer {
  display: inline-grid;
  grid-auto-flow: column;
  grid-template-columns: auto auto auto;
  gap: 0.35ch;
  justify-content: center;
  align-items: center;
  font-size: calc(6rem * var(--m-timer, 1));
  font-weight: 900;
  margin: 16px 0;
  letter-spacing: 0.05em;
}
.countdown-timer .timer-minutes,
.countdown-timer .timer-seconds {
  display: inline-block;
}
.countdown-timer .timer-colon {
  display: inline-block;
  opacity: 0.95;
}

/* Ensure timer content is centered inside its container */
.timer-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* When on break keep the blinds column allocated but hide its content visually */
.blinds-hidden {
  visibility: hidden;
  opacity: 0;
}

/* Secondary stats (approx half the size of the small/big blind) */
.secondary-label { font-size: 0.95rem; color: #666; margin-top: 6px; }
.secondary-value { font-size: calc(var(--blind-size) / 2); font-weight: 700; color: #222; }
.next-level-under { margin-top: 8px; text-align: center; }
.next-level-small strong { font-weight: 800; }

.main-col-right {
  display: flex;
  flex-direction: column;
  align-items: center; /* Center contents so Next Level aligns beneath numeric blinds */
  text-align: center;
}
.blinds-container { font-size: calc(2.5rem * var(--m-blinds, 1)); font-weight: bold; }
  .blinds-section, .ante-section { margin-bottom: 8px; }
  .blinds-section { display: flex; flex-direction: column; align-items: center; }
  .ante-section {
    width: 100%;
    text-align: center;
  }
  .blinds-label { font-size: calc(1.5rem * var(--m-blinds, 1)); color: #666; margin-bottom: 4px; text-align: center; }
  .blinds-values { display: flex; justify-content: center; align-items: center; gap: 12px; }
.small-blind, .big-blind, .ante-value { font-size: calc(var(--blind-size) * var(--blind-scale, 1) * var(--m-blinds, 1)); color: #000; }
.blinds-separator { font-size: calc(2rem * var(--m-blinds, 1)); color: #999; }

/* Placeholder to preserve right-column height/space when no level data is present */
.blinds-placeholder { min-height: calc(var(--blind-size) + 0.5rem); }

/* --- Break --- */
.break-container {
  font-size: 1.25rem;
  color: #666;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 150px;
}
.break-title {
  color: #d32f2f;
  font-size: 1.8rem;
  font-weight: bold;
}

/* --- Display name header --- */
.display-name-header {
  text-align: center;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 16px;
}

/* --- Announcement line --- */
.announcement-line {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1565c0;
  padding: 8px 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Aligns the rebuy notice above the Average Chip Stack column by mirroring
   the stats row's grid tracks exactly (see .tournament-stats-row.three-stats-row). */
.rebuy-notice-row {
  display: grid;
  grid-template-columns: 260px 1fr 260px;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}
.rebuy-notice-row .rebuy-notice-line {
  grid-column: 2;
  justify-self: center;
  min-width: 160px;
  padding-bottom: 0;
}

/* --- Stats Row --- */
.stats-col-left, .stats-col-right {
  text-align: center;
}
.stats-label { font-size: calc(1.2em * var(--m-size, 1)); color: #888; }
.next-level-label { font-size: 1.1em; }
.next-level-details { font-size: 1.1em; color: #333; }
.stats-value { font-size: calc(1.2em * var(--m-size, 1));font-weight: bold; color: #333; }
/* Ensure secondary-value overrides generic stats-value size */
.stats-value.secondary-value { font-size: calc(2.0em * var(--m-size, 1)); }

/* --- Responsive adjustments for smaller screens --- */
@media (max-width: 800px) {
  .tournament-main-row,
  .tournament-stats-row {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }

  .main-col-left,
  .main-col-right,
  .stats-col-left,
  .stats-col-right {
    flex-basis: auto;
    width: 100%;
    max-width: 400px;
    text-align: center;
    align-items: center;
  }

  .timer-blinds-wrapper {
    flex-direction: column;
    align-items: center;
    gap: 24px;
    order: -2; /* Timer and Blinds first */
  }

  .stats-col-center { order: -1; } /* Next Level */
  .stats-col-right { order: 0; } /* Average Chip Stack */
  .main-col-left { order: 1; } /* Payouts */
  .stats-col-left { order: 2; } /* Total Prize Pool */

  .countdown-timer { font-size: calc(4rem * var(--m-timer, 1)); }
  .level-count { font-size: calc(1.8rem * var(--m-level, 1)); }
  .level-heading { font-size: calc(1.8rem * var(--m-level, 1)); }
  .blinds-container { font-size: calc(2rem * var(--m-blinds, 1)); }
  .tournament-main-row { --blind-size: 2.5rem; }
  .announcement-line { font-size: 1.1rem; padding: 6px 12px; }
  .rebuy-notice-row { grid-template-columns: 1fr; }
  .rebuy-notice-row .rebuy-notice-line { grid-column: 1; }

  /* .three-stats-row's grid-template-columns uses fixed pixel tracks (see base rule below)
     that don't fit narrow viewports; collapse to a single centered column here. */
  .tournament-stats-row.three-stats-row {
    grid-template-columns: 1fr !important;
    gap: 12px;
  }
  .three-stats-row .stats-col-left,
  .three-stats-row .stats-col-center,
  .three-stats-row .stats-col-right {
    min-width: 0;
    width: 100%;
    max-width: 400px;
    justify-self: center;
  }
}

/* Tablet landscape optimizations */
@media (min-width: 768px) and (orientation: landscape) {
  .tournament-main-row {
    gap: 20px;
    max-width: 1200px;
    align-items: center;
  }
  .timer-container .countdown-timer {
    font-size: calc(7.5rem * var(--m-timer, 1));
    line-height: 1;
  }
  .level-count { font-size: calc(3.2rem * var(--m-level, 1)); }
  .level-heading { font-size: calc(3.2rem * var(--m-level, 1)); }
  .main-col-left, .main-col-right {
    flex-basis: 220px;
    min-width: 180px;
  }
  .blinds-container { font-size: calc(2.8rem * var(--m-blinds, 1)); }
  .tournament-main-row { --blind-size: 4.8rem; }
  .payouts-title { font-size: calc(1.1rem * var(--m-payouts, 1)); }
}

/* Larger tablets and small laptops in landscape */
@media (min-width: 1000px) and (orientation: landscape) {
  .tournament-main-row {
    gap: 28px;
    max-width: 1400px;
  }
  .timer-container .countdown-timer {
    font-size: calc(9rem * var(--m-timer, 1));
  }
  .level-count { font-size: calc(4rem * var(--m-level, 1)); }
  .level-heading { font-size: calc(4rem * var(--m-level, 1)); }
  .tournament-main-row { --blind-size: 5.2rem; }
}

/* Pre-countdown banner */
.pre-countdown-banner {
  justify-content: center;
}
.pre-countdown-label {
  font-size: 2rem;
  font-weight: 600;
  text-align: center;
  opacity: 0.85;
}
</style>